/**
 * Helpers da obrigatoriedade condicional por valor (requiredWhen).
 *
 * A regra fica no campo DEPENDENTE (o que se torna obrigatório) e referencia,
 * por id, o campo de ORIGEM usado como condição:
 *
 *   requiredWhen = { fieldId, operator, value } | null
 */

// Tipos de campo que podem ser usados como condição, agrupados por "kind".
export const SINGLE_SELECT_TYPES = ['select', 'selectTable', 'selectOrder', 'switchButton']
export const MULTI_SELECT_TYPES = ['selectMultiple']
export const TEXT_TYPES = ['inputText', 'inputArea', 'message']
export const NUMBER_TYPES = ['inputNumber']

/**
 * Classifica o campo de origem no "kind" usado para escolher operadores e o
 * componente de valor. Retorna null quando o tipo não pode ser condição.
 */
export function getConditionKind(field) {
  if (!field) return null

  const type = field.internalTypeName

  if (SINGLE_SELECT_TYPES.includes(type)) return 'select'
  if (MULTI_SELECT_TYPES.includes(type)) return 'multiSelect'
  if (TEXT_TYPES.includes(type)) return 'text'
  if (NUMBER_TYPES.includes(type)) return 'number'

  return null
}

// Kinds cujo valor é escolhido a partir das opções do campo (lista de seleção).
export function isOptionKind(kind) {
  return kind === 'select' || kind === 'multiSelect'
}

function isGroupField(field) {
  return field?.isGroup || field?.internalTypeName === 'group'
}

/**
 * Conjunto de campos dos quais `startId` depende, direta ou transitivamente,
 * seguindo as arestas dependente -> origem (requiredWhen.fieldId).
 */
function collectDependencies(startId, entities) {
  const visited = new Set()
  const stack = [startId]

  while (stack.length) {
    const id = stack.pop()
    if (visited.has(id)) continue
    visited.add(id)

    const sourceId = entities?.[id]?.requiredWhen?.fieldId
    if (sourceId !== undefined && sourceId !== null) stack.push(sourceId)
  }

  visited.delete(startId)
  return visited
}

/**
 * Lista os campos que podem ser usados como ORIGEM da condição para o campo
 * atual, já barrando:
 *  - o próprio campo (auto-dependência);
 *  - grupos e campos inativos;
 *  - tipos incompatíveis;
 *  - candidatos que gerariam dependência circular (o candidato depende, direta
 *    ou transitivamente, do campo atual).
 */
export function getEligibleSourceFields({ fields, currentFieldId }) {
  if (!fields?.ids) return []

  const { ids, entities } = fields

  return ids.reduce((acc, id) => {
    const field = entities[id]

    if (!field) return acc
    if (id === currentFieldId) return acc
    if (isGroupField(field)) return acc
    if (field.active === false) return acc
    if (getConditionKind(field) === null) return acc

    // Evita ciclo: se a origem candidata já depende do campo atual, escolhê-la
    // fecharia um loop de obrigatoriedade.
    if (collectDependencies(id, entities).has(currentFieldId)) return acc

    acc.push(field)
    return acc
  }, [])
}

/** Regra válida = tem campo de origem, operador e valor preenchido. */
export function isRequiredWhenComplete(rule) {
  if (!rule) return false
  if (rule.fieldId === undefined || rule.fieldId === null) return false
  if (!rule.operator) return false
  if (rule.operator === 'filled') return true
  if (rule.value === undefined || rule.value === null || rule.value === '') return false
  return true
}
